// src/components/CreditProfile.js
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Skeleton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { Panel } from "primereact/panel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Confetti from "react-confetti";
import Styles from "../../styles/_global/Styles";
import { RulesEngine } from "../../utils/RulesEngine";
import CreditReportSummary from "../Dashboard/CreditReportSummary";
import CreditReports from "../Dashboard/CreditReports";
import CreditScoreGauge from "../Dashboard/CreditScoreGuage";
import CreditSuggestions from "../Dashboard/CreditSuggestions";
import LiquidGlass from "../LiquidGlass";

export default function CreditProfile() {
  const fetchingRef = useRef(false);
  const noticeTimer = useRef(null);
  const [creditData, setCreditData] = useState({ score: null, risk: null });
  const [scoreExplanations, setScoreExplanations] = useState([]);
  const [reportSummaryData, setReportSummaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadNotice, setShowLoadNotice] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [rules, setRules] = useState([]);
  const [isUnderDebtReview, setIsUnderDebtReview] = useState(false);
  const [hasJudgements, setHasJudgements] = useState(false);
  const [openExplanation, setOpenExplanation] = useState(null);
  const [isSingleColumn, setIsSingleColumn] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));

  const handleToggleExplanation = (index) => {
    setOpenExplanation((prev) => (prev === index ? null : index));
  };

  const confettiThreshold = parseInt(
    process.env.REACT_APP_CONFETTI_THRESHOLD || "700",
    10
  );
  const confettiDisplayTime = parseInt(
    process.env.REACT_APP_CONFETTI_DISPLAY_TIME || "5000",
    10
  );
  const congratulationsTitle =
    process.env.REACT_APP_CONGRATS_TITLE || "Congratulations!";
  const congratulationsMessageTemplate =
    process.env.REACT_APP_CONGRATS_MESSAGE ||
    "You have achieved an excellent credit score!";

  const gaugeHeight = 180;
  const gaugeWidth = 180;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const confettiSettings = {
    numberOfPieces: isMobile ? 150 : 300,
    gravity: isMobile ? 0.1 : 0.2,
  };

  const getDynamicMessage = (template, variables) =>
    template.replace(/{(\w+)}/g, (_, key) => variables[key] || "");

  const getCreditData = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    // start notice timer
    setShowLoadNotice(false);
    noticeTimer.current = setTimeout(() => {
      setShowLoadNotice(true);
    }, 2000);

    const backendUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    const backendApiKey = process.env.REACT_APP_BACKEND_API_KEY;
    const user = JSON.parse(localStorage.getItem("user"));

    try {
      const res = await axios.post(
        `${backendUrl}/api/credit-report`,
        { uuid: user.uuid },
        { headers: { "x-api-key": backendApiKey } }
      );
      const data = res.data.data;
      if (data?.crScore?.length) {
        const scoreData = data.crScore[0];
        const score = scoreData.score;
        const risk = determineRisk(data.crScore, score);
        const bucketId =
          res.data.bucketId ||
          Number(process.env.REACT_APP_CB_DEFAULT_BUCKET_ID) ||
          1;

        const hasShown = localStorage.getItem("confettiShown") === "true";
        if (score >= confettiThreshold && !hasShown) {
          setShowConfetti(true);
          setShowDialog(true);
          localStorage.setItem("confettiShown", "true");
          setTimeout(() => setShowConfetti(false), confettiDisplayTime);
        }

        setCreditData({ data, score, risk });
        setScoreExplanations(data.crScoreExplanation || []);
        setReportSummaryData(data.crAccountDataSummary || []);
        const rulesResult = await RulesEngine.applyRules(data);
        setRules(rulesResult);
        localStorage.setItem("user", JSON.stringify({ ...user, bucketId }));
      }
    } catch (err) {
      console.error(err);
    } finally {
      clearTimeout(noticeTimer.current);
      setShowLoadNotice(false);
      setIsLoading(false);
    }
  }, [confettiThreshold, confettiDisplayTime]);

  useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} - Credit Profile`;
    getCreditData();
    rules.forEach((rule) => {
      if (rule.action.component === "DebtReviewAlert") {
        setIsUnderDebtReview(true);
      }
      if (rule.action.component === "JudgementsAlert") {
        setHasJudgements(true);
      }
    });
  }, [getCreditData, rules]);

  const determineRisk = (arr, score) => {
    if (!arr?.length) return "Unknown";
    const n = parseInt(score, 10);
    if (isNaN(n)) return "Unknown";
    for (let i = 1; i < arr.length; i++) {
      const [min, max] = arr[i].score_Band.split(" - ").map(Number);
      if (n >= min && n <= max) return arr[i].score;
    }
    return "Unknown";
  };

  const renderedCreditScoreGauge = useMemo(() => {
  const GaugeComponent = isMobile ? CreditScoreGauge : CreditScoreGauge;
  return (
    <GaugeComponent
      score={parseInt(creditData.score, 10)}
      risk={creditData.risk}
      width={gaugeWidth}
      height={gaugeHeight}
    />
  );
}, [
  creditData.score,
  creditData.risk,
  gaugeWidth,
  gaugeHeight,
  isMobile, // ← include isMobile so it re-runs when layout changes
]);

  const TYPE_COLOR_MAP = {
    primary: theme.palette.primary.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    success: theme.palette.success.main,
  };

  const renderedScoreExplanations = useMemo(() => {
    const base = [
      {
        title: "How it Works",
        description:
          "Your credit score is a number that represents your creditworthiness.<br/><br/>It is used by lenders to assess the risk of lending you money or extending credit. A higher score indicates lower risk, while a lower score indicates higher risk.<br/><br/>Your score is calculated based on various factors, including your payment history, credit utilization, length of credit history, types of credit accounts, and recent inquiries.",
        type: "primary",
      },
      ...scoreExplanations.map((e) => ({
        title: "Credit Score Insight",
        description: e.scoreExplanation,
        type: "primary",
      })),
    ];

    if (isUnderDebtReview) {
      base.push({
        title: "Under Debt Review",
        description: (
          <>
            You are currently under debt review. This means you are working with
            a debt counselor to manage your debts and improve your financial
            situation.
            <Link
              href="/offers#Debt%20Review"
              sx={{
                color: isMobile ? "#fff" : "#0099cc",
                fontWeight: "bold",
                ml: 1,
              }}
            >
              Debt Review Removal
            </Link>
          </>
        ),
        type: "warning",
      });
      if (hasJudgements) {
        base.push({
          title: "You have Judgements",
          description: (
            <>
              You have judgements against you, which can significantly impact
              your credit score and ability to obtain credit.
              <Link
                href="/offers#Legal"
                sx={{
                  color: isMobile ? "#fff" : "#0099cc",
                  fontWeight: "bold",
                  ml: 1,
                }}
              >
                Lawyer Service
              </Link>
            </>
          ),
          type: "error",
        });
      }
    }

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {base.map((exp, i) => {
            const borderColor = TYPE_COLOR_MAP[exp.type] || Styles.primary.main;
            return (
              <Grid item xs={12} sm={12} key={i}>
                <Button
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  endIcon={openExplanation === i ? <CloseIcon /> : null}
                  onClick={() => handleToggleExplanation(i)}
                  fullWidth
                  sx={{
                    textTransform: "none",
                    justifyContent: "flex-start",
                    color: isMobile ? "#fff" : "inherit",
                    textShadow: isMobile
                      ? "0px 1px 2px rgba(0,0,0,0.5)"
                      : "none",
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                      color: isMobile ? "#fff" : "inherit",
                    },
                    borderBottom: `3px solid ${borderColor}`,
                    borderTop: `1px solid rgba(255,255,255,0.3)`,
                    borderLeft: `1px solid rgba(255,255,255,0.3)`,
                    borderRight: `1px solid rgba(255,255,255,0.3)`,
                    borderRadius: "3px",
                    mb: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  <Typography variant="subtitle1">{exp.title}</Typography>
                </Button>
                <Collapse in={openExplanation === i}>
                  <Box
                    sx={{
                      border: {
                        xs: "1px solid rgba(255,255,255,0.3)",
                        lg: "1px solid #eee",
                      },
                      p: 1,
                      backgroundColor: isMobile
                        ? "rgba(255,255,255,0.3)"
                        : "#f9f9f9",
                      borderRadius: "4px",
                      color: isMobile ? "#fff !important" : "inherit",
                      textShadow: isMobile
                        ? "0px 1px 2px rgba(0,0,0,0.5)"
                        : "none",
                      mb: 2,
                    }}
                  >
                    {typeof exp.description === "string" ? (
                      <Typography
                        variant="body2"
                        dangerouslySetInnerHTML={{
                          __html: exp.description,
                        }}
                      />
                    ) : (
                      exp.description
                    )}
                  </Box>
                </Collapse>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  }, [
    scoreExplanations,
    isUnderDebtReview,
    hasJudgements,
    isSingleColumn,
    isMobile,
    openExplanation,
  ]);

  const alertGlass = isMobile
    ? {
        backgroundColor: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#fff",
        textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
        "& .MuiAlert-icon": { color: "#fff" },
      }
    : {};

  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 1,
        zoom: {
          xs: process.env.REACT_APP_MOBILE_ZOOM_FACTOR || 0.8,
          md: process.env.REACT_APP_DESKTOP_ZOOM_FACTOR || 1,
        },
        overflowX: "hidden",
        pt: 2,
      }}
    >
      {showLoadNotice && (
        <LiquidGlass sx={{ mb: 2 }} altComponent={<Box />} altSx={{ mb: 2 }}>
          <Alert
            icon={<CircularProgress size={20} color="inherit" />}
            severity="info"
            sx={alertGlass}
          >
            We're loading your latest credit profile report. This shouldn't take
            too long.
          </Alert>
        </LiquidGlass>
      )}

      {showConfetti && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          {...confettiSettings}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 9999,
          }}
        />
      )}

      <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
        <DialogTitle>{congratulationsTitle}</DialogTitle>
        <DialogContent>
          <Typography>
            {getDynamicMessage(congratulationsMessageTemplate, {
              threshHold: confettiThreshold,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Box>
            <LiquidGlass
                altComponent={Panel}
                title="Credit Score"
                icon="pi pi-crown"
                sx={{padding: 1}}
                altProps={{
                  header: (
                    <>
                      <i className="pi pi-crown" /> Credit Score
                    </>
                  ),
                }}
              >
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Skeleton
                    variant="circular"
                    width={gaugeWidth}
                    height={gaugeHeight}
                  />
                  <Skeleton
                    variant="text"
                    width={gaugeWidth}
                    height={24}
                    sx={{ mt: 2 }}
                  />
                </Box>
              ) : (
                renderedCreditScoreGauge
              )}
            </LiquidGlass>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Box>
              <LiquidGlass
                altComponent={Panel}
                title="About Your Credit Score"
                icon="pi pi-info-circle"
                sx={{padding: 1}}
                altProps={{
                  header: (
                    <>
                      <i className="pi pi-info-circle" /> About Your Credit
                      Score
                    </>
                  ),
                }}
              >
                {isLoading ? (
                  <Skeleton variant="rounded" width="100%" height={150} />
                ) : (
                  renderedScoreExplanations
                )}
              </LiquidGlass>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Box>
              <CreditReports />
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          {user?.uuid && (
            <Box>
              <CreditSuggestions
                userUuid={user.uuid}
                profileLoaded={!isLoading}
              />
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Box>
              <LiquidGlass
                altComponent={Panel}
                title="Credit Report Summary"
                icon="pi pi-info-circle"
                sx={{padding: 1}}
                altProps={{
                  header: (
                    <>
                      <i className="pi pi-info-circle" /> Credit Report Summary
                    </>
                  ),
                }}
              >
                {isLoading ? (
                  <Skeleton variant="rounded" width="100%" height={200} />
                ) : reportSummaryData[0] ? (
                  <CreditReportSummary
                    summary={reportSummaryData[0]}
                    data={creditData.data}
                  />
                ) : (
                  <Alert
                    severity="info"
                    sx={
                      isMobile
                        ? {
                            backgroundColor: "rgba(255,255,255,0.1)",
                            backdropFilter: "blur(10px)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            color: "#fff",
                            textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
                            "& .MuiAlert-icon": { color: "#fff" },
                          }
                        : {}
                    }
                  >
                    No summary data available.
                  </Alert>
                )}
              </LiquidGlass>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
